// Sprint TENANTS TENANTS.B1 (#2337) — tenant list UI.
//
// Replaces the "Tenants — Soon" disabled sidebar placeholder. Reads
// GET /api/internal/tenants (TENANTS.A1, #2334, already merged) — search,
// tier/status filters, sort, and pagination are all server-side; this page
// holds no client-side filtering of its own.
//
// ── Known, disclosed gaps (do not try to fix here) ──────────────────────────
// seat_count and last_active_at are ALWAYS null on the list response — no
// backing seat-cap wiring or activity-tracking source exists yet (see
// internal.router.ts's own note on GET /tenants). Both render as "—".
// Tracked as #2361 (seat_count — A3's migration 0218 added tenants.seat_limit
// but this endpoint doesn't read it yet) and #2362 (last_active_at). Row
// expand below renders TenantExpandedRow (TENANTS.B2, #2338) — see that
// file's own header.
//
// ── Sort has no direction param (recon finding) ─────────────────────────────
// The API's `sort` enum (name/tier/created/last_active) each carries a FIXED
// server-side direction (name/tier ascending, created/last_active
// descending) — there is no `dir` query param to toggle. Clicking a sortable
// header picks which field to sort by; it does not offer asc/desc toggling,
// because the endpoint has nothing to toggle.
//
// ── Deep link vs. pagination (design decision) ──────────────────────────────
// /internal/tenants/:tenantId must expand that tenant's row even if it does
// not fall on the current page/filter/sort combination. Rather than require
// the caller to already be on the right page, once the main list has loaded
// and the target id is not among its rows, a second request
// (GET /:tenantId, TENANTS.A2) fetches just that tenant and it renders as a
// separate "pinned" row above the table, pre-expanded. If the normal list
// happens to already contain it, no extra request is made and no pinned row
// is shown — see `pinnedTenant` below.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch, ApiError } from '../../lib/api';
import TenantExpandedRow from './TenantExpandedRow';

// ── Types mirroring #2334's response ────────────────────────────────────────

type Tier = 'INDIVIDUAL' | 'STARTER' | 'GROWTH' | 'ENTERPRISE' | 'PLATFORM_PARTNER' | 'ACADEMIC';
type Status = 'Active' | 'Suspended';
type SortField = 'name' | 'tier' | 'created' | 'last_active';

interface TenantRow {
  id: string;
  name: string;
  tier: Tier | string;
  status: Status;
  seat_count: number | null;
  member_count: number;
  last_active_at: string | null;
  created_at: string;
}

interface TenantsListResponse {
  tenants: TenantRow[];
  total: number;
  page: number;
  limit: number;
}

// The subset of TENANTS.A2's detail shape this page needs to build a pinned
// row. Deliberately narrower than the full detail response (no members[],
// no feature_flags, no project_count) — this page only ever renders the
// list-row shape.
interface TenantDetailResponse {
  id: string;
  name: string;
  tier: string;
  status: 'ACTIVE' | 'SUSPENDED';
  created_at: string;
  members: unknown[];
}

const TIERS: Tier[] = ['INDIVIDUAL', 'STARTER', 'GROWTH', 'ENTERPRISE', 'PLATFORM_PARTNER', 'ACADEMIC'];
const LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

function detailToRow(d: TenantDetailResponse): TenantRow {
  return {
    id: d.id,
    name: d.name,
    tier: d.tier,
    // Detail uses ACTIVE/SUSPENDED; the list endpoint (and this page's
    // rendering) uses Active/Suspended — normalize to the one this page uses.
    status: d.status === 'SUSPENDED' ? 'Suspended' : 'Active',
    // Consistent with every other row's disclosed gap (see file header) —
    // showing a real number here while every other row shows "—" would read
    // as an inconsistency, not as this row happening to have richer data.
    seat_count: null,
    member_count: d.members.length,
    last_active_at: null,
    created_at: d.created_at,
  };
}

function loadErrorMessage(err: unknown): string {
  const e = err as ApiError | null;
  return e?.body?.error as string ?? 'Failed to load tenants.';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

// ── Styling helpers — inline styles + CSS vars, per InternalPricingPage ─────

const cellStyle: React.CSSProperties = {
  padding:       '10px 12px',
  borderBottom:  '1px solid var(--border)',
  fontFamily:    'var(--font-body)',
  fontSize:      13,
  color:         'var(--text)',
  verticalAlign: 'top',
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight:    600,
  fontSize:      11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color:         'var(--text-muted)',
  whiteSpace:    'nowrap',
};

function sortHeadStyle(active: boolean): React.CSSProperties {
  return {
    ...headStyle,
    cursor: 'pointer',
    color:  active ? 'var(--text)' : 'var(--text-muted)',
    userSelect: 'none',
  };
}

const inputStyle: React.CSSProperties = {
  padding:      '6px 10px',
  fontSize:     13,
  fontFamily:   'var(--font-body)',
  border:       '1px solid var(--border)',
  borderRadius: 6,
  background:   'var(--bg)',
  color:        'var(--text)',
};

function btnStyle(enabled: boolean): React.CSSProperties {
  return {
    padding:      '5px 12px',
    fontSize:     13,
    fontFamily:   'var(--font-body)',
    border:       '1px solid var(--border)',
    borderRadius: 6,
    background:   enabled ? 'var(--surface)' : 'transparent',
    color:        enabled ? 'var(--text)' : 'var(--text-muted)',
    cursor:       enabled ? 'pointer' : 'not-allowed',
    opacity:      enabled ? 1 : 0.55,
  };
}

function StatusBadge({ status }: { status: Status }) {
  const isActive = status === 'Active';
  return (
    <span
      data-testid={`tenant-status-badge`}
      data-status-tone={isActive ? 'active' : 'suspended'}
      style={{
        display:      'inline-block',
        padding:      '2px 9px',
        borderRadius: 10,
        fontSize:     11,
        fontWeight:   600,
        background:   isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
        color:        isActive ? '#0f9d6e' : '#dc2626',
      }}
    >
      {status}
    </span>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function InternalTenantsPage() {
  const { tenantId: routeTenantId } = useParams<{ tenantId?: string }>();

  const [rawSearch, setRawSearch]     = useState('');
  const [search, setSearch]           = useState('');
  const [tier, setTier]               = useState<Tier | ''>('');
  const [status, setStatus]           = useState<Status | ''>('');
  const [sort, setSort]               = useState<SortField>('created');
  const [page, setPage]               = useState(1);

  const [tenants, setTenants]         = useState<TenantRow[]>([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState('');

  // Seeded once from the URL at mount, not synced via an effect — this is
  // the "you might not need an Effect" case (React docs): the deep-linked
  // row's default-open state is derived from the initial route, not a value
  // that needs to keep tracking routeTenantId for the component's whole
  // lifetime. A later in-app navigation to a DIFFERENT /internal/tenants/:id
  // while this page stays mounted won't re-seed this — out of scope for B1,
  // whose deep link is a fresh page load (bookmark, notification, another
  // tab), not intra-page renavigation.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => (routeTenantId ? new Set([routeTenantId]) : new Set()),
  );
  const [pinnedTenant, setPinnedTenant]   = useState<TenantRow | null>(null);
  const [pinnedLoading, setPinnedLoading] = useState(false);

  // Debounce the raw input into the value actually sent to the server, and
  // reset to page 1 in the same update — a search that changes results
  // should never leave the user stranded on what is now an out-of-range page.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(rawSearch);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [rawSearch]);

  // R1 finding (#2378): the earlier lint-fix pass removed the useRef
  // staleness guard entirely instead of replacing it, reopening a real race
  // — rapid filter/page changes with out-of-order responses could let a
  // stale response overwrite a newer one. `isStale` restores that guard as a
  // closure captured by the EFFECT's own cleanup (mirroring
  // InternalPricingPage.tsx's / useTenantCount's existing `cancelled` flag
  // idiom) rather than a `useRef`, since a `useRef.current` read/write
  // inside a useCallback invoked from a bare effect is what
  // react-hooks/set-state-in-effect actually objects to — a plain
  // per-effect-run closure variable is not.
  const fetchTenants = useCallback(async (isStale: () => boolean) => {
    setLoading(true);
    setLoadError('');

    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (tier) params.set('tier', tier);
    if (status) params.set('status', status);
    params.set('sort', sort);
    params.set('page', String(page));
    params.set('limit', String(LIMIT));

    try {
      const res = await apiFetch<TenantsListResponse>(`/api/internal/tenants?${params.toString()}`);
      if (isStale()) return;
      setTenants(res.tenants ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      if (isStale()) return;
      setLoadError(loadErrorMessage(err));
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [search, tier, status, sort, page]);

  useEffect(() => {
    let cancelled = false;
    fetchTenants(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchTenants]);

  // See file header — a deep-linked tenant absent from the current page gets
  // fetched on its own and rendered as a pinned row instead. Same
  // closure-based staleness guard as fetchTenants, for the same reason.
  const fetchPinnedTenant = useCallback(async (isStale: () => boolean) => {
    if (!routeTenantId || loading) return;
    if (tenants.some(t => t.id === routeTenantId)) return;

    setPinnedLoading(true);
    try {
      const detail = await apiFetch<TenantDetailResponse>(`/api/internal/tenants/${encodeURIComponent(routeTenantId)}`);
      if (isStale()) return;
      setPinnedTenant(detailToRow(detail));
    } catch {
      if (isStale()) return;
      setPinnedTenant(null);
    } finally {
      if (!isStale()) setPinnedLoading(false);
    }
  }, [routeTenantId, tenants, loading]);

  useEffect(() => {
    let cancelled = false;
    fetchPinnedTenant(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchPinnedTenant]);

  // Derived, not effect-synced: once the tenant IS present in the normal
  // list (a filter cleared, a later page loaded it), the pinned row must
  // disappear immediately, not lag a render behind whatever last cleared
  // `pinnedTenant` via setState.
  const visiblePinnedTenant = routeTenantId && !tenants.some(t => t.id === routeTenantId) ? pinnedTenant : null;

  // TENANTS.B3 (#2339) — patches this page's own list-row state after a
  // successful suspend/reactivate/tier action inside an expanded row, so
  // the list's status badge / tier column update immediately without a
  // full page refetch (which would also reset pagination/search/sort).
  const handleTenantUpdated = useCallback((tenantId: string, patch: { status?: 'Active' | 'Suspended'; tier?: string }) => {
    setTenants(prev => prev.map(t => (t.id === tenantId ? { ...t, ...patch } : t)));
    setPinnedTenant(prev => (prev && prev.id === tenantId ? { ...prev, ...patch } : prev));
  }, []);

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setSortField = (field: SortField) => {
    setSort(field);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0 results';
    const start = (page - 1) * LIMIT + 1;
    const end = Math.min(page * LIMIT, total);
    return `${start}–${end} of ${total}`;
  }, [page, total]);

  function renderRow(row: TenantRow, pinned: boolean) {
    const isOpen = expanded.has(row.id);
    return (
      <>
        <tr key={row.id} data-testid={`tenant-row-${row.id}`}>
          <td style={cellStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                onClick={() => toggleExpanded(row.id)}
                aria-expanded={isOpen}
                aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${row.name}`}
                data-testid={`tenant-expand-${row.id}`}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'var(--text-muted)', lineHeight: 1 }}
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              <div style={{ fontWeight: 600 }}>{row.name}</div>
              {pinned && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>(deep-linked, off current page)</span>
              )}
            </div>
          </td>
          <td style={cellStyle}>{row.tier}</td>
          <td style={cellStyle} data-testid={`tenant-seats-${row.id}`}>{row.seat_count ?? '—'}</td>
          <td style={cellStyle} data-testid={`tenant-members-${row.id}`}>{row.member_count}</td>
          <td style={cellStyle}><StatusBadge status={row.status} /></td>
          <td style={cellStyle} data-testid={`tenant-last-active-${row.id}`}>{row.last_active_at ?? '—'}</td>
          <td style={cellStyle}>{fmtDate(row.created_at)}</td>
        </tr>
        {isOpen && (
          <tr key={`${row.id}-detail`} data-testid={`tenant-detail-${row.id}`}>
            <td colSpan={7} style={{ ...cellStyle, background: 'var(--surface-alt)' }}>
              {/* TENANTS.B2 (#2338) — real content. Mounted only while open, so
                  collapse/re-expand naturally remounts and refetches. */}
              <TenantExpandedRow tenantId={row.id} onTenantUpdated={handleTenantUpdated} />
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <div data-testid="internal-tenants-panel">
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
        Tenants
      </h1>

      {/* ── Search + filters ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <input
          type="text"
          value={rawSearch}
          onChange={e => setRawSearch(e.target.value)}
          placeholder="Search tenants by name…"
          aria-label="Search tenants by name"
          data-testid="tenants-search"
          style={{ ...inputStyle, width: 260 }}
        />
        <select
          value={tier}
          onChange={e => { setTier(e.target.value as Tier | ''); setPage(1); }}
          aria-label="Filter by tier"
          data-testid="tenants-filter-tier"
          style={inputStyle}
        >
          <option value="">All tiers</option>
          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={status}
          onChange={e => { setStatus(e.target.value as Status | ''); setPage(1); }}
          aria-label="Filter by status"
          data-testid="tenants-filter-status"
          style={inputStyle}
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Suspended">Suspended</option>
        </select>
      </div>

      {loadError && (
        <div data-testid="tenants-load-error" style={{ padding: '8px 12px', marginBottom: 12, background: 'rgba(239,68,68,0.08)', color: 'var(--error)', borderRadius: 4, fontSize: 13 }}>
          {loadError}
        </div>
      )}

      {pinnedLoading && (
        <div data-testid="tenants-pinned-loading" style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Loading deep-linked tenant…
        </div>
      )}

      {loading ? (
        <div data-testid="tenants-loading" style={{ padding: '4px 0' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              style={{ height: 32, marginBottom: 6, borderRadius: 4, background: 'var(--surface-alt)' }}
            />
          ))}
        </div>
      ) : !loadError && tenants.length === 0 && !visiblePinnedTenant ? (
        <div data-testid="tenants-empty" style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No tenants match the current filters.
        </div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }} data-testid="tenants-table">
            <thead>
              <tr>
                <th style={sortHeadStyle(sort === 'name')} onClick={() => setSortField('name')} data-testid="tenants-sort-name">Tenant Name</th>
                <th style={sortHeadStyle(sort === 'tier')} onClick={() => setSortField('tier')} data-testid="tenants-sort-tier">Tier</th>
                <th style={headStyle}>Seats</th>
                <th style={headStyle}>Members</th>
                <th style={headStyle}>Status</th>
                <th style={sortHeadStyle(sort === 'last_active')} onClick={() => setSortField('last_active')} data-testid="tenants-sort-last_active">Last Active</th>
                <th style={sortHeadStyle(sort === 'created')} onClick={() => setSortField('created')} data-testid="tenants-sort-created">Created</th>
              </tr>
            </thead>
            <tbody>
              {visiblePinnedTenant && renderRow(visiblePinnedTenant, true)}
              {tenants.map(row => renderRow(row, false))}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            <span data-testid="tenants-range">{rangeLabel}</span>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={!hasPrev}
              data-testid="tenants-prev"
              style={btnStyle(hasPrev)}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={!hasNext}
              data-testid="tenants-next"
              style={btnStyle(hasNext)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
